/**
 * Axios instance with interceptors
 */
import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';
import { getOrCreateDeviceId } from '../utils/device';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - Add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken();
    const deviceId = getOrCreateDeviceId();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['X-Device-Id'] = deviceId;
    
    // For FormData requests, don't override the Content-Type header
    // Let axios auto-detect and set multipart/form-data with proper boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check for inactive account and redirect to home
    if (error.response?.status === 401 && error.response?.data?.error === 'Account inactive') {
      tokenStorage.clearTokens();
      window.location.href = '/';
      return Promise.reject(error);
    }

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = tokenStorage.getRefreshToken();
        
        if (refreshToken) {
          // Try to refresh the token
          const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
            refreshToken
          });

          const { token, refreshToken: newRefreshToken } = response.data;
          
          // Store new tokens
          tokenStorage.setTokens(token, newRefreshToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        tokenStorage.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
